import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Request } from '../types';
import { fetchEligibleCounts, fetchCachedDenominators } from '../utils/database';
import { friendlyTime } from '../utils/friendlyTime';

// ─── types ───────────────────────────────────────────────────────────────────

interface CellData {
  num: number;
  den: number;
  pct: number; // 0–100, or -1 if no data
}
interface SolutionRow {
  solution: string;
  cells: Record<string, CellData>;
  total: CellData;
}
interface FocusGroup {
  focus: string;
  solutions: SolutionRow[];
}
interface HeatmapData {
  segments: string[];
  focusGroups: FocusGroup[];
  totals: Record<string, CellData>;
  grandTotal: CellData;
}

// solution → market_area → count
type DenomMap = Map<string, Map<string, number>>;
interface Props {
  requests: Request[];
  accessLevel: AdobeImsAccessLevel;
  onClose: () => void;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// const SOLUTION_BLOCKLIST = new Set(['marketo']);
const SOLUTION_BLOCKLIST = new Set(['asdad']);

// rename to match the product name in the opp details coming from backend
const SOLUTION_RENAME: Record<string, string> = {
  'Genstudio': 'Performance Marketing (GSPeM)',
  'GENSTUDIO': 'Performance Marketing (GSPeM)',
  'GenStudio': 'Performance Marketing (GSPeM)',
  'AEM SITES': 'Sites',
  'AEM ASSETS': 'Assets',
  'AEM FORMS': 'Forms',
  'TARGET': 'Target',
  'COMMERCE': 'Commerce',
  'AJOB2B': 'AJO B2B Edition',
  'MARKETO': 'Marketo',
  'WKFT': 'Workfront',
  'MIX MODELER': 'Mix Modeler',
  'Marketo Engage': 'Marketo'
};

// Fixed focus groups and solution display order. Solutions absent from this list
// are shown under 'Others' at the bottom.
//determines which calls are made to backend
const FOCUS_SOLUTION_ORDER: ReadonlyArray<{
  focus: string;
  solutions: readonly string[];
}> = [{
  focus: 'D&I',
  solutions: ['CJA', 'CJA B2B', 'Analytics', 'Mix Modeler', 'Content Analytics']
}, {
  focus: 'P&A',
  solutions: ['AEP', 'RTCDP', 'Collaboration']
}, {
  focus: 'CJM',
  solutions: ['AJO', 'Campaign', 'Exp Accelerator', 'Target', 'AJO B2B Edition']
}, {
  focus: 'Marketo',
  solutions: ['Marketo', 'Engage', 'Measure']
}, {
  focus: 'AI',
  solutions: ['Agent Orchestrator']
}, {
  focus: 'AEM',
  solutions: ['Sites', 'Assets', 'Forms', 'Sites Optimizer', 'LLM Optimizer', 'Learning Manager']
}, {
  focus: 'Genstudio',
  solutions: ['Performance Marketing (GSPeM)']
}
// { focus: 'Others',        solutions: ['WKFT', 'Planning', 'Commerce','Brand Concierge','MIX MODELER'] }
];
const SOLUTION_TO_FOCUS = new Map<string, string>(FOCUS_SOLUTION_ORDER.flatMap(({
  focus,
  solutions
}) => solutions.map(s => [s, focus])));
const FOCUS_ORDER = FOCUS_SOLUTION_ORDER.map(f => f.focus);
const SOLUTION_ORDER_IN_FOCUS = new Map<string, Map<string, number>>(FOCUS_SOLUTION_ORDER.map(({
  focus,
  solutions
}) => [focus, new Map(solutions.map((s, i) => [s, i]))]));

// Solution names whose slugified form doesn't match their report config ID suffix.
const SOLUTION_REPORT_SLUG_OVERRIDE: Record<string, string> = {
  'LLM Optimizer': 'llmo'
};

// Per-(solution, column) max opp_gnarr threshold (exclusive).
// A deal is counted in a cell only when BOTH conditions hold:
//   1. An entry exists for that (solution, column) pair
//   2. deal.opp_gnarr < threshold
// Empty columns in the CSV = no entry = deal excluded from that cell.
const NUMERATOR_THRESHOLDS: Record<string, Record<string, number>> = {
  'CJA': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000
  },
  'Analytics': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000,
    'Western': 50000
  },
  'RTCDP': {
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000
  },
  'Campaign': {
    'Ind (Key)': 250000,
    'Canada': 250000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 150000,
    'Western': 100000
  },
  'Target': {
    'Ind (Key)': 250000,
    'Canada': 250000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000
  },
  'Engage': {
    'Ind (Key)': 250000,
    'Canada': 250000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000
  },
  'Measure': {
    'Ind (Key)': 250000,
    'Canada': 250000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 100000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000,
    'Western': 100000
  },
  'Sites': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000
  },
  'Assets': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000
  },
  'Forms': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000
  },
  'LLM Optimizer': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000
  },
  'Performance Marketing (GSPeM)': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 100000
  },
  'WKFT': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 150000,
    'Western': 50000
  },
  'WKFT Workflow': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 150000,
    'Western': 50000
  },
  'Planning': {
    'Ind (Key)': 150000,
    'Canada': 150000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000,
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 150000,
    'Western': 50000
  },
  'Commerce': {
    'Ind (Key)': 100000,
    'Canada': 100000,
    'Enterprise': 100000,
    'Scale': 100000,
    'Public Sector': 250000
  },
  'AJOB2B': {
    'UKI': 250000,
    'ME': 750000,
    'SSA': 750000,
    'Central': 150000,
    'Western': 100000
  }
};
function parseProducts(products: string): Array<{
  focus: string;
  solution: string;
}> {
  if (!products) return [];
  return products.split(',').map(p => {
    const t = p.trim();
    if (!t) return null;
    const idx = t.indexOf(':');
    const raw = (idx === -1 ? t : t.substring(idx + 1)).trim();
    if (SOLUTION_BLOCKLIST.has(raw)) return null;
    const solution = SOLUTION_RENAME[raw] ?? raw;

    // const slug = slugify(solution);

    if (!solution) return null;
    // Focus comes from the fixed mapping, not the WF product prefix.
    const focus = SOLUTION_TO_FOCUS.get(solution) ?? 'Others';
    return {
      focus,
      solution
    };
  }).filter((x): x is {
    focus: string;
    solution: string;
  } => x !== null && x.solution.length > 0);
}
function makeCell(num: number, den: number): CellData {
  if (num === 0 && den === 0) return {
    num: 0,
    den: 0,
    pct: -1
  };
  if (den === 0) return {
    num,
    den: 0,
    pct: -1
  };
  return {
    num,
    den,
    pct: num / den * 100
  };
}
function cellBg(cell: CellData): React.CSSProperties {
  if (cell.pct < 0) return {};
  if (cell.den < 5) return {
    background: '#F3F4F6',
    color: '#9CA3AF'
  };
  const p = cell.pct;
  if (p >= 80) return {
    background: '#15803D',
    color: '#fff'
  };
  if (p >= 60) return {
    background: '#16A34A',
    color: '#fff'
  };
  if (p >= 40) return {
    background: '#FCD34D',
    color: '#78350F'
  };
  if (p >= 20) return {
    background: '#EF4444',
    color: '#fff'
  };
  return {
    background: '#B91C1C',
    color: '#fff'
  };
}

// ─── Column key ───────────────────────────────────────────────────────────────
// Which Request field drives heatmap columns (and denominator lookups).
const HEATMAP_COLUMN_KEY: keyof Request = 'opp_segment';

// Only these values may appear as heatmap columns — order here defines display order.
const ALLOWED_COLUMNS = ['Ind (Key)', 'Canada', 'Enterprise', 'Scale', 'Public Sector', 'UKI', 'ME', 'SSA', 'Central', 'Western', 'ANZ', 'Asia', 'Ind', 'Japan', 'No Value'];
const ALLOWED_COLUMNS_SET = new Set(ALLOWED_COLUMNS);
const CENTRAL_AREAS = new Set(['Germany', 'Austria', 'Switzerland', 'Bulgaria', 'Czech Republic', 'Estonia', 'Greece', 'Hungary', 'Poland', 'Romania', 'Russia', 'Slovenia', 'Slovakia', 'Turkey', 'Ukraine']);
const WESTERN_AREAS = new Set(['Belgium', 'Denmark', 'Spain', 'Finland', 'France', 'Italy', 'Lithuania', 'Latvia', 'Luxembourg', 'Netherlands', 'Norway', 'Portugal', 'Sweden']);

// Special routing rules: certain deals are redirected to a named column
// instead of their natural HEATMAP_COLUMN_KEY value.
// Rules are evaluated in order — first match wins.
// Returns undefined when the resolved column is not in the allowlist (request excluded).
function resolveColumnKey(request: Request, columnKey: keyof Request): string | undefined {
  const ma = request.opp_market_area;
  if (ma === 'Canada') return 'Canada';
  if (ma === 'United Kingdom') return 'UKI';
  if (ma === 'Middle East') return 'ME';
  if (ma === 'SSA & Israel') return 'SSA';
  if (ma && CENTRAL_AREAS.has(ma)) return 'Central';
  if (ma && WESTERN_AREAS.has(ma)) return 'Western';
  if (ma === 'ANZ') return 'ANZ';
  if (ma === 'Asia') return 'Asia';
  if (ma === 'Japan') return 'Japan';
  if (request.opp_segment === 'Industry' && request.opp_segment_sales === 'Key') return 'Ind (Key)';
  const raw = request[columnKey] as string | undefined;
  const col = raw && raw.trim() ? raw.trim() : 'No Value';
  return ALLOWED_COLUMNS_SET.has(col) ? col : undefined;
}

// Columns that must always appear even when no deals land there.
const FORCED_COLUMNS = ALLOWED_COLUMNS;

// ─── computeHeatmap ───────────────────────────────────────────────────────────
// Numerator: count requests by (focus||solution, resolvedColumn).
// Denominator: from denomMap when available (live totals), otherwise dummyDen.
// Columns: union of resolved column values from requests + denomMap keys.

function computeHeatmap(requests: Request[], denomMap: DenomMap | null, applyThreshold: boolean, columnKey: keyof Request = HEATMAP_COLUMN_KEY): HeatmapData {
  const numMap = new Map<string, Map<string, Set<string>>>();
  const segSet = new Set<string>();
  const keyOrder: string[] = [];
  for (const request of requests) {
    const seg = resolveColumnKey(request, columnKey);
    if (seg) segSet.add(seg);
    for (const {
      focus,
      solution
    } of parseProducts(request.req_products)) {
      const key = `${focus}||${solution}`;
      if (!numMap.has(key)) {
        numMap.set(key, new Map());
        keyOrder.push(key);
      }
      if (seg) {
        const threshold = NUMERATOR_THRESHOLDS[solution]?.[seg];
        const passes = threshold !== undefined && (!applyThreshold || request.opp_gnarr < threshold);
        if (passes) {
          const m = numMap.get(key)!;
          const ids = m.get(seg) ?? new Set<string>();
          ids.add(request.opp_id);
          m.set(seg, ids);
        }
      }
    }
  }

  // console.log(keyOrder);

  // Always show forced columns even when no requests land there
  for (const col of FORCED_COLUMNS) segSet.add(col);

  // Add keys from denomMap so 0%-covered segments become visible columns
  if (denomMap) {
    for (const [, mMap] of denomMap) {
      for (const seg of mMap.keys()) segSet.add(seg);
    }
  }
  const segments = ALLOWED_COLUMNS.filter(c => segSet.has(c));
  const focusMap = new Map<string, SolutionRow[]>();
  const focusOrder: string[] = [];
  for (const key of keyOrder) {
    const sepIdx = key.indexOf('||');
    const focus = key.substring(0, sepIdx);
    const solution = key.substring(sepIdx + 2);
    if (!focusMap.has(focus)) {
      focusMap.set(focus, []);
      focusOrder.push(focus);
    }
    const segCounts = numMap.get(key)!;
    const cells: Record<string, CellData> = {};
    let totalNum = 0;
    let totalDen = 0;
    for (const seg of segments) {
      const num = segCounts.get(seg)?.size ?? 0;
      const den = denomMap?.get(solution)?.get(seg) ?? 0;
      cells[seg] = makeCell(num, den);
      // Only include segments that have a configured denominator query — segments
      // without one (ANZ, Asia, etc.) would inflate the numerator without a
      // corresponding denominator.
      if (denomMap?.get(solution)?.has(seg)) {
        totalNum += num;
        totalDen += den;
      }
    }
    focusMap.get(focus)!.push({
      solution,
      cells,
      total: makeCell(totalNum, totalDen)
    });
  }

  // Sort focus groups by defined order; 'Others' always last.
  const seenFocuses = new Set(focusOrder);
  const orderedFocuses = [...FOCUS_ORDER.filter(f => seenFocuses.has(f)), ...(seenFocuses.has('Others') ? ['Others'] : []), ...focusOrder.filter(f => f !== 'Others' && !FOCUS_ORDER.includes(f))];
  for (const [focus, rows] of focusMap) {
    const orderMap = SOLUTION_ORDER_IN_FOCUS.get(focus);
    if (orderMap) {
      rows.sort((a, b) => {
        const ai = orderMap.get(a.solution) ?? Infinity;
        const bi = orderMap.get(b.solution) ?? Infinity;
        return ai - bi;
      });
    }
  }
  const focusGroups: FocusGroup[] = orderedFocuses.map(f => ({
    focus: f,
    solutions: focusMap.get(f)!
  }));

  // Totals row: mirror the Total-column fix — only include a solution's contribution
  // to tNum when it has a configured denominator for that segment, so numerator and
  // denominator stay on the same scale.
  const totals: Record<string, CellData> = {};
  for (const seg of segments) {
    let tNum = 0,
      tDen = 0;
    for (const {
      solutions
    } of focusGroups) {
      for (const row of solutions) {
        if (denomMap?.get(row.solution)?.has(seg)) {
          tNum += row.cells[seg]?.num ?? 0;
        }
        tDen += row.cells[seg]?.den ?? 0;
      }
    }
    totals[seg] = makeCell(tNum, tDen);
  }

  // Grand total: sum of the already-fixed solution row Total cells so the scale
  // is consistent with individual rows (both count per-solution occurrences).
  let gNum = 0,
    gDen = 0;
  for (const {
    solutions
  } of focusGroups) {
    for (const row of solutions) {
      gNum += row.total.num;
      gDen += row.total.den;
    }
  }
  return {
    segments,
    focusGroups,
    totals,
    grandTotal: makeCell(gNum, gDen)
  };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({
  size = 20,
  color = '#6B7280'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" style={{
    color
  }} data-id="element-177" data-mp="200r2188s20j3">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-178" data-mp="1m55d112jngqo" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-179" data-mp="1r406cwy50np1" />
    </svg>;
}

// ─── Cell ─────────────────────────────────────────────────────────────────────

function Cell({
  cell
}: {
  cell: CellData;
}) {
  if (cell.pct < 0) return <span style={{
    color: '#9CA3AF',
    fontSize: 10
  }} data-id="element-180" data-mp="24zu90y8bigtp">—</span>;
  const cs = cellBg(cell);
  const greyed = cell.den < 5;
  return <div style={{
    ...cs,
    display: 'inline-block',
    padding: '3px 5px',
    borderRadius: 4,
    textAlign: 'center',
    lineHeight: 1.35,
    minWidth: 46
  }} data-id="element-181" data-mp="2xqvpr3rnskfc">
      <div style={{
      fontSize: 10,
      fontWeight: 700
    }} data-id="element-182" data-mp="32ivlsl4lyt6n">{greyed ? '—' : `${Math.round(cell.pct)}%`}</div>
      <div style={{
      fontSize: 9,
      fontWeight: 400,
      opacity: greyed ? 0.6 : 0.85
    }} data-id="element-183" data-mp="3ccssvskhovue">{cell.num}/{cell.den}</div>
    </div>;
}

// ─── styles ───────────────────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  background: '#374151',
  color: '#F9FAFB',
  fontWeight: 600,
  fontSize: 10,
  textAlign: 'center',
  padding: '7px 8px',
  border: '1px solid #4B5563',
  whiteSpace: 'nowrap'
};
const TD: React.CSSProperties = {
  border: '1px solid #E5E7EB',
  padding: '4px 4px',
  textAlign: 'center',
  verticalAlign: 'middle'
};

// ─── HeatmapModal ─────────────────────────────────────────────────────────────

export function HeatmapModal({
  requests,
  accessLevel,
  onClose
}: Props) {
  const [denomMap, setDenomMap] = useState<DenomMap | null>(null);
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [isLoadingTotals, setIsLoadingTotals] = useState(false);
  const [totalsError, setTotalsError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [applyThreshold, setApplyThreshold] = useState(true);
  const data = useMemo(() => computeHeatmap(requests, denomMap, applyThreshold, HEATMAP_COLUMN_KEY), [requests, denomMap, applyThreshold]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // On mount: load last-persisted denominators from DB (fast, no WF calls).
  useEffect(() => {
    setIsLoadingCache(true);
    void (async () => {
      const solutions = FOCUS_SOLUTION_ORDER.flatMap(({
        solutions
      }) => [...solutions]);
      const settled = await Promise.allSettled(solutions.map(async solution => {
        const reportSlug = SOLUTION_REPORT_SLUG_OVERRIDE[solution] ?? slugify(solution);
        const reportId = `all-eligible-projects-total-${reportSlug}`;
        const result = await fetchCachedDenominators(reportId);
        return {
          solution,
          denominator: result?.heatmapDenominator ?? {},
          lastSyncedAt: result?.lastSyncedAt ?? null
        };
      }));
      const map: DenomMap = new Map();
      let latestSync: string | null = null;
      for (const r of settled) {
        if (r.status === 'fulfilled' && Object.keys(r.value.denominator).length > 0) {
          map.set(r.value.solution, new Map(Object.entries(r.value.denominator)));
          if (r.value.lastSyncedAt && (!latestSync || r.value.lastSyncedAt > latestSync)) {
            latestSync = r.value.lastSyncedAt;
          }
        }
      }
      if (map.size > 0) {
        setDenomMap(map);
        setFromCache(true);
        setLastFetched(latestSync ?? null);
      }
      setIsLoadingCache(false);
    })();
  }, []);

  // Fetch denominator counts for every unique solution in parallel via WF /count API.
  // Report ID pattern: all-eligible-projects-total-<slugified-solution>
  // Each report's countQueries define WF count calls; heatmapDenominator gives market_area → count.
  const handleFetchTotals = useCallback(async () => {
    setIsLoadingTotals(true);
    setTotalsError(null);
    const solutions = FOCUS_SOLUTION_ORDER.flatMap(({
      solutions
    }) => [...solutions]);
    const settled = await Promise.allSettled(solutions.map(async solution => {
      const reportSlug = SOLUTION_REPORT_SLUG_OVERRIDE[solution] ?? slugify(solution);
      const reportId = `all-eligible-projects-total-${reportSlug}`;
      const result = await fetchEligibleCounts(reportId);
      return {
        solution,
        denominator: result?.heatmapDenominator ?? {}
      };
    }));
    const map: DenomMap = new Map();
    let errorCount = 0;
    for (const r of settled) {
      if (r.status === 'fulfilled') {
        const {
          solution,
          denominator
        } = r.value;
        map.set(solution, new Map(Object.entries(denominator)));
      } else {
        errorCount++;
      }
    }
    setDenomMap(map);
    setFromCache(false);
    setLastFetched(new Date().toISOString());
    setIsLoadingTotals(false);
    if (errorCount > 0) {
      setTotalsError(`${errorCount}/${solutions.length} solution(s) failed — partial totals shown`);
    }
  }, [requests]);
  const isLiveTotals = denomMap !== null;
  const noMarketArea = data.segments.length === 0;
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-4 px-2" style={{
    background: 'rgba(0,0,0,0.55)'
  }} onClick={onClose} data-id="element-184" data-mp="24xdbn2rnyu8b">
      <div className="relative bg-white rounded-xl shadow-2xl w-full" style={{
      maxWidth: '98vw'
    }} onClick={e => e.stopPropagation()} data-id="element-185" data-mp="1w2qbal0ux82x">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200" data-id="element-186" data-mp="2nsld3h5wiq8h">
          <div className="flex-1 min-w-0" data-id="element-187" data-mp="2smfsi8eh4j1r">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide" data-id="element-188" data-mp="h5w1bk3b0zd4">
              Coverage Heatmap
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-189" data-mp="c5rktu2ec4mw">
              OnDemand coverage by solution × market area.
              {isLiveTotals ? fromCache ? <span className="ml-1 text-blue-600 font-medium" data-id="element-190" data-mp="lw2qpm5n4nwf">
                    Showing cached totals{lastFetched ? ` — last synced ${friendlyTime(lastFetched)}` : ''}. Click Fetch Live Totals to refresh.
                  </span> : <span className="ml-1 text-emerald-700 font-medium" data-id="element-191" data-mp="3pffknb8f7t2i">
                    Live totals loaded {lastFetched ? friendlyTime(lastFetched) : ''}.
                  </span> : <span className="ml-1 text-amber-600 font-medium" data-id="element-192" data-mp="1jkcrxyxlr7mh">
                  Click Fetch Live Totals to load coverage percentages.
                </span>}
            </p>
            {totalsError && <p className="text-xs text-red-600 mt-0.5" data-id="element-193" data-mp="1lp9f6d31en98">{totalsError}</p>}
            {noMarketArea && !isLoadingTotals && <p className="text-xs text-amber-700 mt-0.5" data-id="element-194" data-mp="1qlq3p8e5f3mu">
                No market_area data in deals — add it to the main report fieldMap to see coverage by segment.
              </p>}
          </div>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0" data-id="element-195" data-mp="20jnyyfufe0oy">
            <label className="flex items-center gap-1.5 cursor-pointer select-none" title="When on, deals are only counted if opp_gnarr is below the configured threshold for that cell" data-id="element-196" data-mp="12zjljtb0g7iw">
              <div onClick={() => setApplyThreshold(v => !v)} className={`relative w-8 h-4 rounded-full transition-colors ${applyThreshold ? 'bg-emerald-500' : 'bg-slate-300'}`} data-id="element-197" data-mp="1cve992u3cssj">
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${applyThreshold ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-198" data-mp="k78qumqiffvv" />
              </div>
              <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-199" data-mp="32gbnnnh65pwl">Min deal value</span>
            </label>
            {accessLevel === 'editor' || accessLevel === 'admin' || accessLevel === 'superadmin' ? <button onClick={() => void handleFetchTotals()} disabled={isLoadingTotals || isLoadingCache} className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5" data-id="element-200" data-mp="2ti9q5hwn9fe2">
                {isLoadingTotals ? <><Spinner size={12} color="#fff" data-id="element-201" data-mp="2ankyty1tf3hd" /> Fetching…</> : isLiveTotals ? '↺ Refresh Totals' : 'Fetch Live Totals'}
              </button> : null}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-base leading-none font-bold" aria-label="Close" data-id="element-202" data-mp="1vser96n2mwlx">
              ✕
            </button>
          </div>
        </div>

        {/* Loading overlay — covers table during mount cache fetch or live WF fetch */}
        {(isLoadingCache || isLoadingTotals) && <div className="flex flex-col items-center justify-center gap-3 py-16" data-id="element-203" data-mp="3r3xxflh8b4ca">
            <Spinner size={36} color="#059669" data-id="element-204" data-mp="3r1uvq46prmrq" />
            <p className="text-xs text-slate-500" data-id="element-205" data-mp="3d3wtty9kxit4">
              {isLoadingCache ? 'Loading cached totals…' : 'Fetching live totals…'}
            </p>
          </div>}

        {/* Table */}
        {!isLoadingCache && !isLoadingTotals && data.focusGroups.length === 0 ? <div className="p-10 text-center text-slate-400 text-sm" data-id="element-206" data-mp="384jyep4n73ex">
            No data available. Sync live data first.
          </div> : !isLoadingCache && !isLoadingTotals ? <div className="overflow-x-auto px-2 py-3" data-id="element-207" data-mp="16dgmlwj0niss">
            <table style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: 11
        }} data-id="element-208" data-mp="16d6xeb5spfo5">
              <thead data-id="element-209" data-mp="2qwj2j39uhcmc">
                <tr data-id="element-210" data-mp="2qtohb9j0bnrv">
                  <th style={{
                ...TH,
                textAlign: 'left',
                width: 72
              }} data-id="element-211" data-mp="2lwoc10tzep1t">Focus</th>
                  <th style={{
                ...TH,
                textAlign: 'left',
                width: 130
              }} data-id="element-212" data-mp="2lulc9p311hjw">Solution</th>
                  {data.segments.length === 0 ? <th style={{
                ...TH,
                width: 100,
                color: '#9CA3AF'
              }} data-id="element-213" data-mp="2vv3zmttsvg6k">Market Area</th> : data.segments.map(s => <th key={s} style={{
                ...TH,
                width: 65
              }} data-id="element-214" data-mp="39lhbv8dq5h1p">{s}</th>)}
                  <th style={{
                ...TH,
                background: '#1F2937',
                width: 65
              }} data-id="element-215" data-mp="35knnyf0a828t">Total</th>
                </tr>
              </thead>
              <tbody data-id="element-216" data-mp="3jkybb82duyp8">
                {data.focusGroups.map(({
              focus,
              solutions
            }) => solutions.map((row, i) => <tr key={`${focus}|${row.solution}`} style={{
              background: i % 2 === 0 ? '#fff' : '#FAFAFA'
            }} data-id="element-217" data-mp="3elv7ts21x0jd" data-mpi={i}>
                      {i === 0 && <td rowSpan={solutions.length} style={{
                ...TD,
                background: '#4B5563',
                color: '#F9FAFB',
                fontWeight: 700,
                fontSize: 10,
                textAlign: 'center',
                verticalAlign: 'middle',
                letterSpacing: '0.03em'
              }} data-id="element-218" data-mp="1k7u9r9ebshkc">
                          {focus}
                        </td>}
                      <td style={{
                ...TD,
                textAlign: 'left',
                fontWeight: 500,
                color: '#374151',
                background: '#F9FAFB'
              }} data-id="element-219" data-mp="23xlexqi1tjs">
                        {row.solution}
                      </td>
                      {data.segments.length === 0 ? <td style={TD} data-id="element-220" data-mp="c13f8dqhzynp"><span style={{
                  color: '#9CA3AF',
                  fontSize: 10
                }} data-id="element-221" data-mp="c0k5vlewyila">—</span></td> : data.segments.map(seg => <td key={seg} style={TD} data-id="element-222" data-mp="kufamsc4i70e">
                              <Cell cell={row.cells[seg] ?? {
                  num: 0,
                  den: 0,
                  pct: -1
                }} data-id="element-223" data-mp="ktx6zan4sqig" />
                            </td>)}
                      <td style={{
                ...TD,
                background: '#F3F4F6'
              }} data-id="element-224" data-mp="usmhgicp78oo">
                        <Cell cell={row.total} data-id="element-225" data-mp="usw9scl7trxj" />
                      </td>
                    </tr>))}
              </tbody>
              <tfoot data-id="element-226" data-mp="zx6yrs091qkb">
                <tr style={{
              background: '#F3F4F6'
            }} data-id="element-227" data-mp="zvvl90h054g0">
                  <td colSpan={2} style={{
                ...TD,
                textAlign: 'left',
                fontWeight: 700,
                color: '#374151',
                background: '#E5E7EB'
              }} data-id="element-228" data-mp="uy3sk48j9ear">
                    Totals
                  </td>
                  {data.segments.length === 0 ? <td style={TD} data-id="element-229" data-mp="1nlzrlflkriap" /> : data.segments.map(seg => <td key={seg} style={TD} data-id="element-230" data-mp="1inohd3rdb5ry"><Cell cell={data.totals[seg]} data-id="element-231" data-mp="1iohnq3mezt9k" /></td>)}
                  <td style={{
                ...TD,
                background: '#E5E7EB'
              }} data-id="element-232" data-mp="1dw6fmdhit15x"><Cell cell={data.grandTotal} data-id="element-233" data-mp="1duvaa98fflcp" /></td>
                </tr>
                <tr style={{
              background: '#F9FAFB'
            }} data-id="element-234" data-mp="19v4luc6dhhbc">
                  <td colSpan={2} style={{
                ...TD,
                textAlign: 'left',
                fontWeight: 700,
                color: '#374151',
                background: '#E5E7EB'
              }} data-id="element-235" data-mp="26e29svdv76n2">
                    % of deals
                  </td>
                  {data.segments.length === 0 ? <td style={TD} data-id="element-236" data-mp="2la2qvqkr2kpn" /> : data.segments.map(seg => <td key={seg} style={{
                ...TD,
                fontWeight: 700,
                color: '#374151',
                fontSize: 11
              }} data-id="element-237" data-mp="2q2m9x327i81u">
                          {data.totals[seg].pct >= 0 ? `${Math.round(data.totals[seg].pct)}%` : '—'}
                        </td>)}
                  <td style={{
                ...TD,
                fontWeight: 700,
                color: '#374151',
                background: '#E5E7EB',
                fontSize: 11
              }} data-id="element-238" data-mp="341c86m0ridrs">
                    {data.grandTotal.pct >= 0 ? `${Math.round(data.grandTotal.pct)}%` : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div> : null}

        {/* Legend */}
        <div className="px-6 py-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-239" data-mp="2z0y0x66zj7ut">
          <div className="flex items-center gap-3 text-xs text-slate-600" data-id="element-240" data-mp="1in7rdjx3jrds">
            <span className="font-semibold text-slate-500" data-id="element-241" data-mp="1nncazitax288">Coverage:</span>
            {[{
            bg: '#B91C1C',
            label: '0–20%'
          }, {
            bg: '#EF4444',
            label: '20–40%'
          }, {
            bg: '#FCD34D',
            textColor: '#78350F',
            label: '40–60%'
          }, {
            bg: '#16A34A',
            label: '60–80%'
          }, {
            bg: '#15803D',
            label: '80%+'
          }].map(({
            bg,
            textColor = '#fff',
            label
          }) => <div key={label} className="flex items-center gap-1" data-id="element-242" data-mp="apvbx0wiscum">
                <div style={{
              width: 12,
              height: 12,
              background: bg,
              borderRadius: 2,
              flexShrink: 0
            }} data-id="element-243" data-mp="3j3u7w2jhry84" />
                <span style={{
              color: textColor === '#fff' ? '#374151' : textColor
            }} data-id="element-244" data-mp="3e624ocdecpom">{label}</span>
              </div>)}
          </div>
          <p className="text-xs text-slate-400 italic ml-auto" data-id="element-245" data-mp="3rys2rf679l23">
            Num = OnD records · Den = total eligible deals · — = no data or &lt;5 volume
          </p>
        </div>
      </div>
    </div>;
}